<template>
  <div class="meal-feedback-form">
    <!-- Confirmation -->
    <div v-if="submitted" class="feedback-success" role="status">
      <p class="success-icon" aria-hidden="true">✅</p>
      <h3 class="success-title">Merci !</h3>
      <p class="success-subtitle">Votre avis a bien été enregistré.</p>
      <button type="button" class="btn btn-secondary" @click="editAgain">Modifier mon avis</button>
    </div>

    <form v-else @submit.prevent="submit">
      <p v-if="!canSubmit" class="feedback-notice" role="status">
        Sélectionnez une cantine et un service pour donner votre avis.
      </p>

      <p v-if="existingFeedbackId" class="feedback-notice" role="status">
        Vous avez déjà donné votre avis pour ce service : l'envoi mettra à jour votre avis.
      </p>

      <!-- Appréciation générale -->
      <fieldset class="feedback-fieldset">
        <legend class="feedback-legend">Comment était le repas ?</legend>

        <div class="emoji-grid">
          <button
            v-for="item in ratings"
            :key="item.value"
            type="button"
            class="emoji-btn"
            :class="{ active: rating === item.value }"
            :aria-pressed="String(rating === item.value)"
            @click="rating = item.value"
          >
            <span class="emoji-large" aria-hidden="true">{{ item.emoji }}</span>
            <span class="emoji-label">{{ item.label }}</span>
          </button>
        </div>
      </fieldset>

      <!-- Plat préféré -->
      <fieldset class="feedback-fieldset">
        <legend class="feedback-legend">Qu'avez-vous préféré ?</legend>

        <div class="dishes-list">
          <button
            v-for="item in dishes"
            :key="item.id"
            type="button"
            class="dish-option"
            :class="{ active: preferredDish === item.id }"
            :aria-pressed="String(preferredDish === item.id)"
            @click="preferredDish = item.id"
          >
            <span class="dish-check" aria-hidden="true">{{ preferredDish === item.id ? "✓" : "○" }}</span>
            <span class="dish-label">{{ item.label }}</span>
          </button>
        </div>
      </fieldset>

      <!-- Commentaire -->
      <div class="feedback-fieldset">
        <label :for="commentFieldId" class="feedback-legend">Un commentaire ?</label>
        <textarea
          :id="commentFieldId"
          v-model="comment"
          class="comment-input"
          rows="3"
          placeholder="C'était délicieux !"
        />
      </div>

      <p v-if="errorMessage" class="feedback-error" role="alert">{{ errorMessage }}</p>

      <button type="submit" class="btn btn-primary" :disabled="!canSend" :class="{ disabled: !canSend }">
        {{ submitLabel }}
      </button>
    </form>
  </div>
</template>

<script>
/**
 * Formulaire de retour repas (capacité `meal-feedback`), volontairement
 * **hôte-agnostique** : il ne lit ni le store pour deviner la cantine, ni la route.
 * La cantine, la date et le service lui sont fournis par son hôte — dashboard de
 * personnel via `DashboardContextHeader`, ou écran convive.
 *
 * Un retour repas est une donnée personnelle : aucun `console.log` de son contenu.
 */
let uid = 0

export default {
  name: "MealFeedbackForm",

  props: {
    canteenId: {
      type: [Number, String],
      default: null,
    },
    serviceId: {
      type: [Number, String],
      default: null,
    },
    date: {
      type: String,
      default: null,
    },
  },

  data() {
    uid += 1
    return {
      instanceId: uid,
      rating: null,
      preferredDish: "plat",
      comment: "",
      submitted: false,
      isSaving: false,
      errorMessage: null,
      existingFeedbackId: null,
      ratings: [
        { value: 5, emoji: "😍", label: "Excellent" },
        { value: 4, emoji: "😊", label: "Très bon" },
        { value: 3, emoji: "😐", label: "Correct" },
        { value: 2, emoji: "😕", label: "Moyen" },
        { value: 1, emoji: "😞", label: "Pas bon" },
      ],
      dishes: [
        { id: "entree", label: "Entrée" },
        { id: "plat", label: "Plat principal" },
        { id: "accomp", label: "Accompagnement" },
        { id: "dessert", label: "Dessert" },
      ],
    }
  },

  computed: {
    commentFieldId() {
      return `meal-feedback-comment-${this.instanceId}`
    },

    mealDate() {
      return this.date || new Date().toISOString().split("T")[0]
    },

    canSubmit() {
      // Le service est requis par l'API : sans lui, rien n'est envoyable.
      return Boolean(this.canteenId) && Boolean(this.serviceId)
    },

    canSend() {
      return this.canSubmit && Boolean(this.rating) && !this.isSaving
    },

    submitLabel() {
      if (this.isSaving) return "Envoi en cours…"
      return this.existingFeedbackId ? "Mettre à jour mon avis" : "Envoyer mon avis"
    },
  },

  watch: {
    canteenId: "loadExistingFeedback",
    serviceId: "loadExistingFeedback",
    date: "loadExistingFeedback",
  },

  mounted() {
    this.loadExistingFeedback()
  },

  methods: {
    /**
     * Récupère l'avis déjà donné pour ce couple date / service, afin de proposer sa
     * modification plutôt que d'échouer sur la contrainte d'unicité.
     */
    async loadExistingFeedback() {
      this.resetState()
      if (!this.canSubmit) return
      try {
        const existing = await this.$store.dispatch("fetchMyMealFeedback", {
          canteenId: this.canteenId,
          date: this.mealDate,
          serviceId: this.serviceId,
        })
        if (existing) {
          this.existingFeedbackId = existing.id
          this.rating = existing.rating
          this.preferredDish = existing.preferredDish || "plat"
          this.comment = existing.comment || ""
        }
        this.$emit("existing-feedback", existing ? existing.id : null)
      } catch {
        // Silencieux : l'absence d'avis préexistant n'est pas une erreur bloquante,
        // et le contenu d'un retour ne doit pas atterrir dans la console.
        this.existingFeedbackId = null
      }
    },

    resetState() {
      this.existingFeedbackId = null
      this.rating = null
      this.preferredDish = "plat"
      this.comment = ""
      this.errorMessage = null
      this.submitted = false
    },

    async submit() {
      if (!this.canSend) return
      this.isSaving = true
      this.errorMessage = null

      // `respondent_kind` n'est volontairement pas envoyé : il est déduit du compte
      // côté serveur puis figé.
      const payload = {
        rating: this.rating,
        preferred_dish: this.preferredDish,
        comment: this.comment,
        meal_date: this.mealDate,
        service: Number(this.serviceId),
      }

      try {
        const saved = this.existingFeedbackId
          ? await this.$store.dispatch("updateMealFeedback", {
              canteenId: this.canteenId,
              id: this.existingFeedbackId,
              payload,
            })
          : await this.$store.dispatch("createMealFeedback", {
              canteenId: this.canteenId,
              payload,
            })
        this.existingFeedbackId = saved && saved.id ? saved.id : this.existingFeedbackId
        this.submitted = true
        this.$emit("saved", { id: this.existingFeedbackId })
      } catch {
        this.errorMessage = "Votre avis n'a pas pu être enregistré. Réessayez dans un instant."
      } finally {
        this.isSaving = false
      }
    },

    editAgain() {
      this.submitted = false
    },
  },
}
</script>

<style scoped>
.meal-feedback-form {
  text-align: left;
}

.feedback-success {
  text-align: center;
  padding: 1rem 0;
}

.success-icon {
  font-size: 3rem;
  margin: 0 0 0.5rem 0;
}

.success-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #065f46;
  margin: 0 0 0.25rem 0;
}

.success-subtitle {
  color: #4b5563;
  margin: 0 0 1.5rem 0;
}

.feedback-fieldset {
  border: none;
  padding: 0;
  margin: 0 0 1.5rem 0;
}

.feedback-legend {
  display: block;
  font-size: 1.05rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 0.75rem;
  padding: 0;
}

.feedback-notice {
  background: #eff6ff;
  border-left: 4px solid #2563eb;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  color: #1e3a8a;
  font-size: 0.9rem;
  margin: 0 0 1.25rem 0;
}

.feedback-error {
  background: #fef2f2;
  border-left: 4px solid #dc2626;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  color: #991b1b;
  font-size: 0.9rem;
  margin: 0 0 1rem 0;
}

.emoji-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
}

@media (min-width: 480px) {
  .emoji-grid {
    grid-template-columns: repeat(5, 1fr);
  }
}

.emoji-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  padding: 0.75rem 0.4rem;
  background: #f9fafb;
  border: 3px solid transparent;
  border-radius: 12px;
  cursor: pointer;
}

.emoji-btn:hover {
  background: #eff6ff;
}

/* L'état sélectionné n'est pas codé par la seule couleur : bordure + aria-pressed. */
.emoji-btn.active {
  background: #dbeafe;
  border-color: #1d4ed8;
  font-weight: 700;
}

.emoji-btn:focus-visible,
.dish-option:focus-visible,
.btn:focus-visible {
  outline: 3px solid #1d4ed8;
  outline-offset: 2px;
}

.emoji-large {
  font-size: 2.25rem;
}

.emoji-label {
  font-size: 0.72rem;
  color: #4b5563;
  text-align: center;
}

.dishes-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.dish-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #f9fafb;
  border: 3px solid transparent;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
}

.dish-option:hover {
  background: #eff6ff;
}

.dish-option.active {
  background: #dbeafe;
  border-color: #1d4ed8;
  font-weight: 700;
}

.dish-check {
  font-size: 1.25rem;
  color: #1d4ed8;
}

.dish-label {
  font-size: 0.95rem;
  color: #1a1a1a;
}

.comment-input {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #d1d5db;
  border-radius: 10px;
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
}

.comment-input:focus-visible {
  outline: 3px solid #1d4ed8;
  outline-offset: 1px;
}

.btn {
  width: 100%;
  padding: 0.9rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
}

.btn-primary {
  background: #1d4ed8;
  color: white;
}

.btn-primary.disabled {
  background: #d1d5db;
  color: #6b7280;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f3f4f6;
  color: #1a1a1a;
}
</style>
